// The batch check's selection-level summaries (moved out of the server's check service by BL-2437),
// so the server check and the phone's offline check (`offline-batch-check`) word a refusal and
// count phases one way. Pure (ADR 010 — Isomorphic Services).
import type { BatchBlockReason, LotPhaseKind } from '@auctogon/core/isomorphic/lifecycle';
import type { BatchCheckResponse, BatchIneligibleRecord } from '@auctogon/core-admin/schemas/responses/lot-batch';

import { LOT_PHASE_ORDER } from '../constants/status-legend';

/**
 * The terminal-immutability family: the reasons `canApplyLotTransition` emits for
 * a lot blocked because it has already resolved. BL-1293 AC 4 split `sold` out of
 * the generic `terminal` so an all-sold selection can say "already sold"; this set
 * is what lets a MIXED terminal selection fall back to the generic phrasing rather
 * than to "not eligible" (see {@link uniformReason}).
 */
const TERMINAL_FAMILY_REASONS: ReadonlySet<BatchBlockReason> = new Set<BatchBlockReason>([
  'terminal',
  'sold-lock',
]);

/**
 * The single reason when every ineligible record shares one cause, else undefined.
 *
 * BL-1293 AC 4: when the reasons are NOT uniform but every one is in the terminal
 * family, collapse to `terminal` instead of giving up. Without this a selection
 * blocked by one sold lot and one withdrawn lot would report no uniform reason,
 * and `blockedTooltip` would degrade from the honest "N of M selected lots are
 * already sold, unsold, withdrawn, passed, or deleted" to a bare "not eligible" —
 * a regression introduced by the split, not an improvement. `terminal` is the
 * family's generic member, so it is the correct answer for a mixed set.
 *
 * @param ineligible - The entry's ineligible records.
 * @returns The one reason to show, or undefined.
 */
export function uniformReason(ineligible: BatchIneligibleRecord[]): BatchBlockReason | undefined {
  if (ineligible.length === 0) return undefined;
  // BL-1430 (M5): the selection-level sold lock OUTRANKS both the uniformity
  // check and the terminal-family collapse below. The card asks for exactly this
  // — "the new sold-source reason must survive that collapse when the blocker
  // really is 'a sold lot is selected'" — because without it a selection holding
  // one sold lot and one withdrawn lot has non-uniform reasons, falls to the
  // family fallback, and tells the operator "already sold, unsold, withdrawn,
  // passed, or deleted" when the actionable cause is one specific thing.
  if (ineligible.some((i) => i.reason === 'sold-in-selection')) return 'sold-in-selection';
  const first = ineligible[0].reason;
  if (ineligible.every((i) => i.reason === first)) return first;
  return ineligible.every((i) => TERMINAL_FAMILY_REASONS.has(i.reason)) ? 'terminal' : undefined;
}

/**
 * BL-1909 — the selection's phases with a count each, most common first, ties in
 * lifecycle order (`LOT_PHASE_ORDER`) so the confirm reads the same way every
 * time. A lot with no derivable phase (`null`) is left out rather than guessed.
 *
 * @param kinds - Each selected lot's phase kind.
 * @returns `[{ phase, count }]`, sorted.
 */
export function countPhaseKinds(kinds: ReadonlyArray<LotPhaseKind | null>): BatchCheckResponse['phaseCounts'] {
  const counts = new Map<LotPhaseKind, number>();
  for (const kind of kinds) {
    if (kind) counts.set(kind, (counts.get(kind) ?? 0) + 1);
  }
  const ladder = (k: LotPhaseKind) => LOT_PHASE_ORDER.indexOf(k);
  return [...counts]
    .map(([phase, count]) => ({ phase, count }))
    .sort((a, b) => b.count - a.count || ladder(a.phase) - ladder(b.phase));
}
